import TYPES from '@/types';
import Vue from 'vue';
import { convertToLargerPossibleUnit } from '@/vue-app/utils/files';

// Infrastructure
import Functions from '@/modules/shared/infrastructure/utils/functions';

// Application
import ProofOfAddressService
  from '@/modules/user/customer-address/application/services/proof-of-address-service';
import ChooseYourDocumentsService
  from '@/modules/user/customer-address/application/services/choose-your-documents-service';

// Domain
import Inject from '@/modules/shared/domain/di/inject';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';

export default class UpdateProofOfAddressAttachYourDocumentViewModel {
  @Inject(TYPES.PROOF_OF_ADDRESS_SERVICE)
  private readonly proof_of_address_service!: ProofOfAddressService

  @Inject(TYPES.CHOOSE_YOUR_DOCUMENTS_SERVICE)
  private readonly choose_your_documents_service!: ChooseYourDocumentsService;

  @Inject(TYPES.I18N)
  readonly translator!: Translator;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  @Inject(TYPES.UTIL_FUNCTIONS)
  readonly functions!: Functions;

  readonly view!: Vue;

  readonly i18n_namespace = 'components.user-profile.user-profile-tabs.user-profile-my-documents-tab.update-proof-of-address.attach-your-document';

  public constructor(view: Vue) {
    this.view = view;
  }

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  readonly limit_file_size = 4 * 1024 * 1024;

  small_description = this.translate('description_3');

  address_proof: File | null = null;

  invalid_file = true;

  minimum_file_size = 0.1;

  allowed_extensions = ['image/jpeg', 'image/jpg', 'image/png'];

  address_proof_accept = 'image/png, image/jpeg, image/jpg';

  initialize = () => {
    this.address_proof = this.proof_of_address_service.getProofOfAddressFileData();
    this.invalid_file = (!this.address_proof);
    const { pocket_option_is_checked, wealth_option_is_checked } = this
      .choose_your_documents_service.getChooseYourDocumentsData();
    if (pocket_option_is_checked && wealth_option_is_checked) {
      this.small_description += `${this.translate('allianz')} y ${this.translate('kuspit')}.`;
    } else {
      this.small_description += (wealth_option_is_checked) ? `${this.translate('allianz')}.` : `${this.translate('kuspit')}.`;
    }
  }

  get styledFileName(): string {
    if (this.address_proof) {
      return `${this.address_proof.name.slice(0, 30)}${this.address_proof.name.length > 35 ? '...' : ''}`;
    }

    return this.translate('attach_placeholder');
  }

  get styledFileSize(): string {
    if (this.address_proof) {
      return convertToLargerPossibleUnit(this.address_proof.size);
    }

    return '0 kb';
  }

  setBase64FileToState = async () => {
    if (this.address_proof) {
      const base_64_file = await this.convertFileToBase64(this.address_proof);
      if (base_64_file) {
        this.proof_of_address_service.setProofOfAddressData({
          name: this.address_proof.name,
          mime_type: this.address_proof.type,
          file_data: base_64_file.toString(),
        });
      }
    }
  }

  handleAddressProof = (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      const [file] = target.files;
      if (file.size <= this.limit_file_size && file.size >= this.minimum_file_size
        && this.allowed_extensions.indexOf(file.type) > -1) {
        this.address_proof = file;
        this.invalid_file = false;
      } else {
        this.address_proof = null;
        this.invalid_file = true;
        this.message_notifier.showErrorNotification(this.translate('errors.invalid_file'));
      }
      this.proof_of_address_service.setProofOfAddressFileData(this.address_proof);
    }
  }

  convertFileToBase64 = async (selected_file: File) => (
    this.functions.convert_file_to_base_64(selected_file)
  );

  endProcess = async () => {
    await this.setBase64FileToState();
    this.view.$emit('endProcess');
  }

  prevStep = () => {
    this.view.$emit('prevStep');
  }
}
